// WYVIO UI 1.0 — Mandats · Liste (Lot M1)
// Route canonique : /app/mandats
// Squelette M1 : liste vide-state + création d'un mandat (vendeur inline + véhicule).
// Aucune logique commerciale, aucun compteur, aucun badge — M1 = fondations.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ArrowRight, FileSignature } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/context/AuthContext";
import { AppSidebar } from "@/components/AppSidebar";
import { mandatesApi } from "@/lib/api/mandates";
import CreateMandateDialog from "@/components/mandats/CreateMandateDialog";
import StatusPill from "@/components/mandats/StatusPill";
import { fmtMoney } from "@/lib/money";

const STATUS_LABEL = {
  draft:            "Brouillon",
  onboarding:       "En préparation",
  commercializing:  "En commercialisation",
  reserved:         "Réservé",
  archived:         "Archivé",
};

const STATUS_DOT = {
  draft:            "#94A3B8",
  onboarding:       "#F59E0B",
  commercializing:  "#C8FF2F",
  reserved:         "#8B5CF6",
  archived:         "#64748B",
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return ""; }
};

const vehicleLine = (v = {}) => {
  const bits = [v.brand, v.model, v.version].filter(Boolean);
  return bits.join(" ") || "Véhicule non renseigné";
};

const vehicleSub = (v = {}) => {
  const bits = [];
  if (v.year) bits.push(String(v.year));
  if (typeof v.mileage === "number") bits.push(`${v.mileage.toLocaleString("fr-FR")} km`);
  if (v.energy) bits.push(v.energy);
  return bits.join(" · ");
};

const MandateRow = ({ m, onOpen }) => {
  const status = m.status || "draft";
  return (
    <article
      className="relative flex items-center gap-6 pl-4"
      data-testid={`mandat-row-${m.id}`}
    >
      <span
        className="absolute left-0 top-3 bottom-3 w-[3px] rounded-full opacity-60"
        style={{ background: STATUS_DOT[status] || "#94A3B8" }}
      />
      <button
        onClick={() => onOpen(m.id)}
        className="relative h-[96px] w-[152px] flex-shrink-0 overflow-hidden rounded-2xl bg-[#0C0C0C]"
        data-testid={`mandat-row-photo-${m.id}`}
        aria-label={`Ouvrir mandat ${m.reference}`}
      >
        <div
          className="flex h-full w-full items-center justify-center text-white/25"
          style={{ background: "radial-gradient(circle at 50% 60%, #1c1c1c 0%, #0a0a0a 75%)" }}
        >
          <FileSignature size={28} strokeWidth={1.5} />
        </div>
      </button>

      <button onClick={() => onOpen(m.id)} className="min-w-0 max-w-[440px] flex-1 text-left">
        <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
          <h3 className="text-[18px] font-medium tracking-[-0.01em] text-black">
            {vehicleLine(m.vehicle)}
          </h3>
          <span className="truncate text-[12.5px] text-black/40 font-mono">
            {m.reference}
          </span>
        </div>
        <div className="mt-2 text-[13px] text-black/55">
          {vehicleSub(m.vehicle) || "—"}
        </div>
      </button>

      <div className="hidden text-right lg:block">
        <div className="flex items-center gap-1.5 justify-end">
          <StatusPill status={status} testId={`mandat-row-status-${m.id}`} />
          {m.publications?.is_published && (
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[9.5px] font-medium uppercase tracking-[0.12em] text-emerald-700"
                  data-testid={`mandat-row-published-badge-${m.id}`}>
              · publié
            </span>
          )}
        </div>
        <div className="mt-1 text-[11.5px] text-black/35">
          {m.pricing_initialized && m.pricing?.asking_price_current
            ? fmtMoney(m.pricing.asking_price_current, m.pricing.currency)
            : "Prix non fixé"}
        </div>
      </div>

      <button
        onClick={() => onOpen(m.id)}
        className="inline-flex flex-shrink-0 items-center gap-2 rounded-full bg-white px-4 py-2 text-[12.5px] font-medium text-black/75 transition-colors hover:text-black"
        style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.03), inset 0 0 0 1px rgba(0,0,0,0.05)" }}
        data-testid={`mandat-row-open-${m.id}`}
      >
        Ouvrir
        <ArrowRight size={13} />
      </button>
    </article>
  );
};

const Skel = () => (
  <div className="flex items-center gap-6 pl-4">
    <div className="h-[96px] w-[152px] animate-pulse rounded-2xl bg-black/[0.04]" />
    <div className="min-w-0 flex-1 max-w-[440px] space-y-2.5">
      <div className="h-4 w-2/5 animate-pulse rounded-full bg-black/[0.05]" />
      <div className="h-3 w-1/3 animate-pulse rounded-full bg-black/[0.04]" />
    </div>
    <div className="h-9 w-24 animate-pulse rounded-full bg-black/[0.04]" />
  </div>
);

export default function MandatsListV3() {
  const navigate = useNavigate();
  const { user, agency } = useAuth();
  const [mandats, setMandats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const data = await mandatesApi.list();
      setMandats(data);
    } catch (e) {
      toast.error(e.message || "Impossible de charger les mandats");
      setMandats([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const rows = useMemo(() => mandats || [], [mandats]);

  return (
    <div
      className="relative min-h-screen text-black"
      style={{
        background:
          "radial-gradient(1000px 600px at 100% 0%, rgba(200,255,47,0.04) 0%, rgba(200,255,47,0) 45%), linear-gradient(180deg, #FAFAF7 0%, #F5F4EF 100%)",
      }}
      data-testid="mandats-list-page"
    >
      <AppSidebar agency={agency} user={user} testId="mandats-sidebar" />

      <main className="lg:pl-[260px]">
        <div className="mx-auto max-w-[1120px] px-10 py-12">
          <div className="flex items-start justify-between gap-6">
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-[0.16em] text-black/45">
                M1 · Fondations
              </div>
              <h1 className="mt-3 text-[38px] font-semibold leading-[1.02] tracking-[-0.02em] text-black">
                Mandats
              </h1>
              <p className="mt-1.5 text-[14px] text-black/50">
                Cockpit de commercialisation des véhicules avant réservation.
              </p>
            </div>

            <button
              onClick={() => setDialogOpen(true)}
              className="inline-flex flex-shrink-0 items-center gap-2 rounded-full bg-[#C8FF2F] px-4 py-2 text-[13px] font-medium text-black transition-transform hover:scale-[1.01]"
              style={{ boxShadow: "0 4px 10px -6px rgba(200,255,47,0.4)" }}
              data-testid="mandats-new-btn"
            >
              <Plus size={14} /> Nouveau mandat
            </button>
          </div>

          <div className="mt-12 flex items-baseline gap-6 border-b border-black/[0.06] pb-3">
            <span className="text-[11px] font-medium uppercase tracking-[0.2em] text-black">
              Tous les mandats
            </span>
            {rows.length > 0 && (
              <span className="text-[11px] uppercase tracking-[0.18em] text-black/35">
                {rows.length} au total
              </span>
            )}
          </div>

          <section className="mt-6 space-y-8" data-testid="mandats-list-rows">
            {loading && [0, 1, 2].map((i) => <Skel key={i} />)}
            {!loading && rows.length === 0 && (
              <div
                className="rounded-3xl bg-white/60 py-16 text-center"
                style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.04)" }}
                data-testid="mandats-empty-state"
              >
                <FileSignature size={28} className="mx-auto text-black/25" strokeWidth={1.5} />
                <div className="mt-5 text-[15px] font-medium text-black">
                  Aucun mandat pour l&apos;instant
                </div>
                <p className="mx-auto mt-2 max-w-[380px] text-[13px] text-black/50">
                  Créez votre premier mandat pour commencer à commercialiser un véhicule
                  pour le compte d&apos;un vendeur particulier ou professionnel.
                </p>
                <button
                  onClick={() => setDialogOpen(true)}
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-black px-4 py-2 text-[13px] font-medium text-white transition-opacity hover:opacity-90"
                  data-testid="mandats-empty-create-btn"
                >
                  <Plus size={14} /> Créer un mandat
                </button>
              </div>
            )}
            {!loading && rows.map((m) => (
              <MandateRow
                key={m.id}
                m={m}
                onOpen={(id) => navigate(`/app/mandat/${id}`)}
              />
            ))}
          </section>

          <div className="h-24" />
        </div>
      </main>

      <CreateMandateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreated={(m) => {
          setDialogOpen(false);
          toast.success(`Mandat ${m.reference} créé`);
          navigate(`/app/mandat/${m.id}`);
        }}
      />
    </div>
  );
}
